import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useAlertLog } from "@/api/districts";
import type { AlertMeterType, MeterAlert } from "@/api/districts/types";
import { CopyableCell } from "@/components/shared/copyable-cell";
import type { ColumnDef } from "@/components/table";
import { DataTable } from "@/components/table";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDistrict } from "@/contexts/district-context";
import { useDebounce } from "@/hooks/use-debounce";
import { getAlertDescriptionKey } from "@/lib/alert-descriptions";

const DEFAULT_PAGE_SIZE = 20;

const TYPE_BADGE_VARIANTS: Record<AlertMeterType, "water" | "heat"> = {
	water: "water",
	heat: "heat",
};

function parseTypeParam(value: string | null): AlertMeterType | "all" {
	if (value === "water" || value === "heat") return value;
	return "all";
}

export function AlertsPage() {
	const { t, i18n } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const typeFilter = parseTypeParam(searchParams.get("type"));
	const initialSearch = searchParams.get("search") ?? "";

	const [search, setSearch] = useState(initialSearch);
	const [ordering, setOrdering] = useState("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const debouncedSearch = useDebounce(search);
	const { districtId } = useDistrict();

	const { data, isLoading } = useAlertLog({
		type: typeFilter === "all" ? undefined : typeFilter,
		serial_number: debouncedSearch || undefined,
		ordering: ordering || undefined,
		page,
		page_size: pageSize,
		district_id: districtId ?? undefined,
	});

	function updateTypeFilter(value: string) {
		const next = new URLSearchParams(searchParams);
		if (value === "all") {
			next.delete("type");
		} else {
			next.set("type", value);
		}
		setSearchParams(next, { replace: true });
		setPage(1);
	}

	const typeSelect = (
		<Select value={typeFilter} onValueChange={updateTypeFilter}>
			<SelectTrigger className="w-44">
				<SelectValue placeholder={t("alerts.filterType")} />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">{t("alerts.typeAll")}</SelectItem>
				<SelectItem value="water">{t("alerts.typeWater")}</SelectItem>
				<SelectItem value="heat">{t("alerts.typeHeat")}</SelectItem>
			</SelectContent>
		</Select>
	);

	const columns: ColumnDef<MeterAlert>[] = [
		{
			key: "meter_serial_number",
			label: t("meterAlerts.colSerialNumber"),
			sortField: "meter_serial_number",
			render: (row) => (
				<CopyableCell
					value={row.meter_serial_number}
					label={t("meterAlerts.colSerialNumber")}
				/>
			),
		},
		...(typeFilter === "all"
			? [
					{
						key: "type" as const,
						label: t("meterAlerts.colMeterType"),
						render: (row: MeterAlert) =>
							row.type ? (
								<Badge variant={TYPE_BADGE_VARIANTS[row.type]}>
									{t(`alerts.type${row.type === "water" ? "Water" : "Heat"}`)}
								</Badge>
							) : (
								<span>—</span>
							),
					},
				]
			: []),
		{
			key: "alarm",
			label: t("meterAlerts.colAlertType"),
			sortField: "alarm",
			render: (row) => {
				const descKey = getAlertDescriptionKey(row.alarm);
				if (!descKey) return <span>{row.alarm}</span>;
				return (
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="cursor-help underline decoration-dotted decoration-muted-foreground/60 underline-offset-4">
								{row.alarm}
							</span>
						</TooltipTrigger>
						<TooltipContent>{t(descKey)}</TooltipContent>
					</Tooltip>
				);
			},
		},
		{
			key: "state",
			label: t("meterAlerts.colState"),
			sortField: "state",
			render: (row) => <span>{row.state.toFixed(3)}</span>,
		},
		{
			key: "cap_dt",
			label: t("meterAlerts.colDate"),
			sortField: "cap_dt",
			render: (row) =>
				new Intl.DateTimeFormat(i18n.language, {
					dateStyle: "short",
					timeStyle: "short",
				}).format(new Date(row.cap_dt)),
		},
	];

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:p-6">
			<DataTable
				data={data}
				columns={columns}
				loading={isLoading}
				page={page}
				pageSize={pageSize}
				onPageChange={(p) => {
					setPage(p);
				}}
				onPageSizeChange={(size) => {
					setPageSize(size);
					setPage(1);
				}}
				search={search}
				onSearchChange={(value) => {
					setSearch(value);
					setPage(1);
				}}
				searchPlaceholder={t("alerts.searchBySerialNumber")}
				ordering={ordering}
				onOrderingChange={(o) => {
					setOrdering(o);
					setPage(1);
				}}
				headerActions={typeSelect}
			/>
		</div>
	);
}
